import { z } from 'zod'

/**
 * How many rows a list page holds unless the caller says otherwise.
 *
 * One number for the client and the server, so no two lists — and no two
 * layers — disagree about what a page is. Fifty because it is well past what
 * fits on a screen: paging is for lists that are long, and a page that ends
 * before the scroll does makes people page to look rather than to move.
 */
export const DEFAULT_PAGE_SIZE = 50

/**
 * The most rows one request may ask for.
 *
 * A ceiling rather than a silent clamp: over it the request is rejected, so a
 * caller that asks for a thousand rows is told, instead of being handed two
 * hundred and left to believe that was all of them.
 */
export const MAX_PAGE_SIZE = 200

/**
 * Which page of a list, and how big a page is.
 *
 * **Both optional, and omitting them returns the whole list.** That is
 * deliberate, not laziness: these are the same endpoints the public API serves,
 * and tokens are already in use against them. Defaulting to a page would
 * silently truncate every existing caller's data — a listing that used to be
 * complete quietly becoming its first fifty rows is the worst kind of breaking
 * change, because nothing errors. Paging is something a caller opts into.
 *
 * `page` is 1-based, because it is a number people read and type. `coerce`
 * because a query string only carries strings; a page of "two" is still
 * rejected rather than coerced to NaN, since Zod's coercion of a
 * non-numeric string fails the `number` check.
 */
const paginationFields = {
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(MAX_PAGE_SIZE).optional(),
}

/**
 * A list query: `page`/`limit` plus whatever else that list is filtered by.
 *
 * A builder rather than a schema to extend, because `paginationQuerySchema`
 * carries a `refine` and a refined schema cannot be extended — the page rule
 * would have to be restated in every list's query, which is exactly how one of
 * them ends up not having it.
 */
/**
 * A page asked for without a size has no meaning.
 *
 * Exported as a rule to apply rather than baked into the builder: Zod cannot
 * infer the argument of a `refine` over a generic shape, so the check has to be
 * attached where the shape is concrete. Attaching it *is* the requirement, and
 * `every list query enforces the page rule` in the tests is what stops one
 * being written without it.
 */
export const PAGE_NEEDS_LIMIT: [
  (value: { page?: number | undefined; limit?: number | undefined }) => boolean,
  { message: string; path: PropertyKey[] },
] = [
  (value) => value.page === undefined || value.limit !== undefined,
  { message: 'Asking for a page needs a limit, or the page has no size.', path: ['limit'] },
]

/**
 * A list query: `page`/`limit` plus whatever else that list is filtered by.
 *
 * Strict, so a typo is a rejection rather than a filter that silently does
 * nothing. Finish it with `.refine(...PAGE_NEEDS_LIMIT)`.
 */
export function paginatedQuery<Shape extends z.ZodRawShape>(shape: Shape) {
  return z.object({ ...paginationFields, ...shape }).strict()
}

export const paginationQuerySchema = paginatedQuery({}).refine(...PAGE_NEEDS_LIMIT)

export type PaginationQuery = z.infer<typeof paginationQuerySchema>

/** How many rows to skip and how many to take, or nothing for the whole list. */
export interface PageSlice {
  skip: number
  take: number
}

/**
 * The slice a query asks for, or `undefined` for "everything".
 *
 * `undefined` rather than a slice with an enormous `take`, so a repository can
 * spread it into a Prisma call and get exactly the query it had before paging
 * existed — no `skip: 0, take: Infinity` to reason about, and no change to the
 * plan for callers that do not page.
 */
export function pageSlice(query: PaginationQuery | undefined): PageSlice | undefined {
  if (!query?.limit) return undefined
  const page = query.page ?? 1
  return { skip: (page - 1) * query.limit, take: query.limit }
}
